import { useEffect, useRef, useState } from 'react';
import { AppShell } from '@/components/layout/AppShell';
import { LevelWave } from '@/components/LevelWave';
import { MicGate } from '@/components/MicGate';
import { Card } from '@/components/ui';
import { TUNER } from '@/config/audio';
import { t } from '@/i18n';
import { audioEngine } from '@/audio/engine/AudioEngine';
import { useAudioState } from '@/hooks/useAudio';
import { TunerSmoother, stringFrequency, type TunerDisplay } from '@/training/tuner';
import type { StringNumber } from '@/types/music';
import './TunerPage.css';

const STRINGS: { number: StringNumber; label: string }[] = [
  { number: 6, label: 'E2' },
  { number: 5, label: 'A2' },
  { number: 4, label: 'D3' },
  { number: 3, label: 'G3' },
  { number: 2, label: 'B3' },
  { number: 1, label: 'E4' },
];

const EMPTY: TunerDisplay = { noteName: null, cents: null, inTune: false, stringNumber: null };

/**
 * Monophonic tuner. One string at a time — which is how guitars are tuned.
 *
 * This screen exists to protect the rest of the app: an out-of-tune guitar
 * reads as bad playing in every training mode, and a beginner cannot tell the
 * difference. The DSP is YIN on the same decimated stream the chord analysis
 * already runs; see src/audio/dsp/pitch.ts.
 */
export function TunerPage() {
  return (
    <AppShell title={t('tuner.title')} showBack>
      <MicGate>
        <TunerView />
      </MicGate>
    </AppShell>
  );
}

function TunerView() {
  const audio = useAudioState();
  const [display, setDisplay] = useState<TunerDisplay>(EMPTY);
  const smootherRef = useRef(new TunerSmoother());

  useEffect(() => {
    if (audio.status !== 'ready') return;
    const smoother = smootherRef.current;
    smoother.reset();
    audioEngine.setTuner(true);
    const unsubscribe = audioEngine.onPitch((pitch) => {
      setDisplay(smoother.push(pitch));
    });
    return () => {
      unsubscribe();
      audioEngine.setTuner(false);
    };
  }, [audio.status]);

  const cents = display.cents;
  const clamped =
    cents === null
      ? 0
      : Math.max(-TUNER.displayRangeCents, Math.min(TUNER.displayRangeCents, cents));
  const needleShift = (clamped / TUNER.displayRangeCents) * 50; // −50%..+50%

  const statusKey =
    cents === null
      ? ('tuner.listening' as const)
      : display.inTune
        ? ('tuner.inTune' as const)
        : cents < 0
          ? ('tuner.low' as const)
          : ('tuner.high' as const);

  return (
    <>
      <Card section className="tuner__stage">
        <p className={`tuner__note${display.noteName ? '' : ' tuner__note--empty'}`}>
          {display.noteName ?? '—'}
        </p>
        <p className="tuner__cents tnum" aria-hidden="true">
          {cents === null ? ' ' : `${cents > 0 ? '+' : ''}${Math.round(cents)} ¢`}
        </p>

        <div className="tuner__scale" aria-hidden="true">
          <span className="tuner__zone" />
          <span className="tuner__tick tuner__tick--mid" />
          <span className="tuner__tick" style={{ left: '25%' }} />
          <span className="tuner__tick" style={{ left: '75%' }} />
          <span
            className={`tuner__needle${display.inTune ? ' tuner__needle--good' : ''}${
              cents === null ? ' tuner__needle--idle' : ''
            }`}
            style={{ left: `${50 + needleShift}%` }}
          />
        </div>

        <p className={`tuner__status tuner__status--${display.inTune ? 'good' : 'plain'}`} role="status">
          {t(statusKey)}
        </p>
      </Card>

      <div className="tuner__strings" role="group" aria-label={t('tuner.strings')}>
        {STRINGS.map((item) => (
          <span
            key={item.number}
            className={`tuner__string${
              display.stringNumber === item.number
                ? display.inTune
                  ? ' tuner__string--good'
                  : ' tuner__string--active'
                : ''
            }`}
          >
            <span className="tuner__string-name">{item.label}</span>
            <span className="tuner__string-hz tnum">{stringFrequency(item.number).toFixed(0)}</span>
          </span>
        ))}
      </div>

      <div className="listening listening--on">
        <LevelWave bars={30} height={26} paused={false} />
      </div>

      <p className="tuner__prompt">{t('tuner.prompt')}</p>
    </>
  );
}
